import { PLAYBACK_POLL_INTERVAL_MS } from '@mixwire/contracts';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { fetchPartyView } from '../api/client.js';
import { partyViewQueryKey } from '../api/queryKeys.js';
import { useReportPoll } from '../sync/usePollCycle.js';

import type { PartySlug, PartyView } from '@mixwire/contracts';

/**
 * The interval is the server's, not this page's: every response says when to
 * come back, aimed just past the next Spotify sample the server is going to
 * take. A fixed interval of our own would drift against that schedule and spend
 * half its requests re-reading a sample it already had. The contract's constant
 * is only the fallback for the first request, before anything has been said.
 *
 * `staleTime: 0` because the schedule is the freshness policy here — anything
 * higher would have the timer fire and get the cached answer back.
 */
export const usePartyView = (slug: PartySlug): UseQueryResult<PartyView> => {
  const query = useQuery({
    queryKey: partyViewQueryKey(slug),
    queryFn: async () => fetchPartyView(slug),
    refetchInterval: (current) =>
      current.state.data?.sync.nextPollInMs ?? PLAYBACK_POLL_INTERVAL_MS,
    staleTime: 0,
  });

  useReportPoll(query.data?.sync, query.dataUpdatedAt);

  return query;
};
